import { z } from "zod";

/**
 * Layer 0 (E4-4) Path B — the `identity_claim` notification contract (rides the generic
 * `user_notifications` table, migration 082). The backend similarity scorer writes the payload; the
 * Dashboard SPA renders the "is this you?" prompt and the click → the claim route. Defined once here so
 * the scorer, the claim route, and the SPA share ONE shape (no hand-typed literals).
 *
 * SPA COPY (lockstep §0.6): kept byte-for-byte in sync with the MVP source of truth at
 * `lib/dashboard/contracts/identity-claim.ts`. Match it EXACTLY — `.nullish()` not `.optional()`.
 *
 * The recipient (notification.user_id) is the Terum user the ghost is suggested to BE. `target_person_id`
 * is that user's verified person (the merge TARGET); `candidate_person_id` is the unclaimed ghost
 * (the merge SOURCE). The claim derives the target from the authenticated user — never from this payload —
 * so a forged payload cannot retarget the merge.
 */
export const IDENTITY_CLAIM_NOTIFICATION_TYPE = "identity_claim" as const;

export const IdentityClaimEvidenceSchema = z
  .object({
    signals: z.array(z.string()).default([]),
    display_name: z.string().nullish(),
    shared_collaborator_count: z.number().nullish(),
    shared_channel_count: z.number().nullish(),
  })
  .passthrough();

export const IdentityClaimPayloadSchema = z
  .object({
    dedup_key: z.string(),
    team_id: z.string(),
    candidate_person_id: z.string().uuid(),
    target_person_id: z.string().uuid(),
    evidence: IdentityClaimEvidenceSchema,
  })
  .passthrough();

export type IdentityClaimEvidence = z.infer<typeof IdentityClaimEvidenceSchema>;
export type IdentityClaimPayload = z.infer<typeof IdentityClaimPayloadSchema>;

/** The claim route's request body — the user confirms which suggested ghost is them. */
export const IdentityClaimRequestSchema = z.object({
  candidate_person_id: z.string().uuid(),
});
export type IdentityClaimRequest = z.infer<typeof IdentityClaimRequestSchema>;
